'use client'

import { useTranslation } from '@/lib/i18n/useTranslation.client'
import { fetchVideoThumbnailUrl } from '@/lib/video/video.utils'
import type { Image as ImageType } from '@gql-generated/graphql'
import { Media } from '@gql-generated/graphql'
import { useAtomValue } from 'jotai'
import { useNextIsClientHydrated } from '@/lib/next/useNextIsClientHydrated'
import { useEffect, useMemo, useState } from 'react'
import { productArticlesAtom, selectedReferenceAtom } from '../../services/product.store'
import { ProductImageGallerySkeleton } from './ProductImageGallerySkeleton.client'
import type { Slide } from './ProductImageSlider.client'
import { ProductImageSlider } from './ProductImageSlider.client'
import {
  PRODUCT_IMAGE_SIZE_DESKTOP,
  PRODUCT_IMAGE_SIZE_THUMBNAIL,
} from '../../services/product.constants'

const placeholderImage = {
  alt: 'Image',
  set: [],
}

export type ProductImageGalleryProps = {
  brandLogo: ImageType | null | undefined
  images: ImageType[] | null | undefined
  videoUrls?: string[] | null
  className?: string
}

export function ProductImageGallery({
  brandLogo,
  images: defaultImages,
  videoUrls,
  className,
}: ProductImageGalleryProps): React.ReactElement | null {
  const isMounted = useNextIsClientHydrated()
  const { t } = useTranslation(['productImageGallery'])
  const selectedReference = useAtomValue(selectedReferenceAtom)
  const articles = useAtomValue(productArticlesAtom)

  const parsedVideoUrls = useMemo(() => videoUrls ?? [], [videoUrls])

  const [videoThumbnails, setVideoThumbnails] = useState<{ mobile: string; desktop: string }[]>([])

  useEffect(() => {
    if (!parsedVideoUrls.length) return

    async function fetchThumbnails() {
      const results = await Promise.all(
        parsedVideoUrls.map(async (url) => {
          const [mobile, desktop] = await Promise.all([
            fetchVideoThumbnailUrl(url, PRODUCT_IMAGE_SIZE_THUMBNAIL),
            fetchVideoThumbnailUrl(url, PRODUCT_IMAGE_SIZE_DESKTOP),
          ])

          return { mobile, desktop }
        }),
      )

      setVideoThumbnails(results)
    }

    fetchThumbnails()
  }, [parsedVideoUrls])

  const images = useMemo(() => {
    if (selectedReference) {
      return articles.find((article) => article.reference === selectedReference)?.mainImage
    }

    if (defaultImages) {
      return defaultImages
    }

    return []
  }, [articles, selectedReference, defaultImages])

  const slides = (Array.isArray(images) ? images : images ? [images] : [])
    .map((image) => {
      const imageSet = image.set ?? []

      if (imageSet.length === 0) {
        return null
      }

      const mainImage = {
        alt: image.alt ?? '',
        set: imageSet.filter((s) => s.media === Media.Mobile || s.media === Media.Desktop),
      }

      // TODO: Back team needs to provide a new thumb image size
      const thumbImage = {
        alt: image.alt ?? '',
        set: imageSet.filter((s) => s.media === Media.Mobile),
      }

      return {
        mainImage,
        thumbImage,
      }
    })
    .filter(Boolean) as Slide[]

  // If there are no images, display a placeholder image
  if (!slides.length) {
    slides.push({
      mainImage: placeholderImage,
      thumbImage: placeholderImage,
    })
  }

  if (!isMounted) return <ProductImageGallerySkeleton />

  const videoSlides = parsedVideoUrls.map((url, index) => {
    const thumbnails = videoThumbnails[index]
    const videoThumbnailImage = {
      alt: t('productImageGallery:videoAlt'),
      set: thumbnails
        ? [
            { url: thumbnails.mobile, media: Media.Mobile },
            { url: thumbnails.desktop, media: Media.Desktop },
          ]
        : [],
    }

    return {
      mainImage: videoThumbnailImage,
      thumbImage: videoThumbnailImage,
      videoUrl: url,
    }
  })

  return (
    <ProductImageSlider
      brandLogo={brandLogo}
      slides={[...slides, ...videoSlides]}
      className={className}
    />
  )
}
